import type { Core } from "@/client/core/Core.api";
import { audienceStateSignal } from "@/client/core/services/feature-flags.signal";
import { createFetchSpliceSamplesService } from "@/client/core/services/fetch-splice-samples.service";
import { createLibrarySearchService } from "@/client/core/services/library-search.service";
import { createAudioToPresetSearchService } from "@/client/core/services/audio-to-preset-search.service";
import { createSearchWithSoundSimilarService } from "@/client/core/services/search-with-sound-similar.service";
import { createSpliceSoundsSearchService } from "@/client/core/services/splice-sounds-search.service";
import { createCoreStorage } from "@/client/core/services/storage.service";
import { createSvgIcon } from "@/client/ui/design-system/icons";
import { createEl } from "@/client/utils/createEl";
import { getDisplayMode } from "@/client/utils/getDisplayMode";
import { createSeedCard } from "./seed-card.component";
import { createAudioFileInputEl } from "@/client/ui/components/audio-file-input.component";
import { addFileDropHandling } from "@/client/utils/handleFileDropped";
import { createSignal } from "@/client/utils/signal";
import { px } from "@/client/utils/units";
import { isObject } from "@/shared/utils/typeGuards";
import type { SpliceSearchResult } from "@/shared/splice-search.api";
import {
    getSeededModes,
    getSoundsBrowserMode,
    isSeededMode,
    kDefaultSoundsBrowserModeId,
    getTextModes,
    hasSeededMode,
    type SoundsBrowserMode,
} from "./modes/sounds-browser-modes";
import { createSampleRow, type SampleRowComponent } from "./sample-row.component";
import { createSoundsBrowserFilters } from "./sounds-browser-filters.component";
import { loadSoundsBrowserSession } from "./sounds-browser-session";
import { createSamplePreviewCache } from "./sample-preview-cache";
import { createSamplePreviewPlayer, sweepAuditionResources } from "./sample-preview-player";
import {
    clampSoundsBrowserWidth,
    createSoundsBrowserActions,
    hasActiveFilters,
    kDefaultSoundsBrowserFilters,
    kSoundsBrowserDefaultWidth_px,
} from "./sounds-browser-actions";
import type {
    SoundsBrowserContext,
    SoundsBrowserPrefs,
    SoundsBrowserSeed,
    SoundsBrowserSignals,
    SoundsBrowserState,
} from "./sounds-browser.api";
import * as styles from "./sounds-browser.css";
import { createWaveSpinner } from "@/client/ui/components/wave-spinner.component";
import { kNarrowViewportQuery } from "@/client/utils/displayMode";
import { getSampleTargets, type SampleTarget } from "@/client/utils/sample-targets";
import { registerSoundsBrowserOpener } from "@/client/utils/sounds-browser-opener";
import { attachSheetSwipe } from "./sheet-swipe";

/** The most Atlantis will return per page: it rejects a larger `count` outright. */
const kResultsPerPage = 16;

const kQuickPicks = ["drums", "bass", "vocals", "piano", "guitar", "synth", "pad", "808"];

const isSoundsBrowserPrefs = (value: unknown): value is SoundsBrowserPrefs =>
    isObject(value) && typeof value.isOpen === "boolean" && typeof value.width_px === "number";

const kDefaultPrefs: SoundsBrowserPrefs = {
    isOpen: false,
    width_px: kSoundsBrowserDefaultWidth_px,
};

/**
 * The Splice Sounds browser as the dock on the left of every website page:
 * search, filter and audition the catalog, then drag a sample into any
 * dropzone. The retrieval capabilities are modes of its one search field
 * (see `modes/sounds-browser-modes.ts`), each gated by audience the way an
 * app is. Mount once per page from the entry file, after the app's UI:
 *
 * ```ts
 * mountSoundsBrowser(core);
 * ```
 *
 * A no-op in embedded hosts. The Sounds plugin's Creative Partner panel and
 * the Labs plugin sit beside the real Sounds browser, and a second one inside
 * a 400px column would be neither wanted nor fit.
 */
export const mountSoundsBrowser = (core: Core): void => {
    if (getDisplayMode() !== "web") {
        return;
    }

    const prefs = createCoreStorage<SoundsBrowserPrefs>("sounds-browser", {
        version: 1,
        defaultState: kDefaultPrefs,
        validate: isSoundsBrowserPrefs,
    });
    const saved = prefs.load();

    const state: SoundsBrowserState = {
        isOpen: saved.isOpen,
        width_px: clampSoundsBrowserWidth(saved.width_px),
        modeId: kDefaultSoundsBrowserModeId,
        visibleModes: [],
        seed: undefined,
        selectedUuid: undefined,
        resume: undefined,
        query: "",
        filters: kDefaultSoundsBrowserFilters,
        results: [],
        tagSummary: [],
        isSearching: false,
        isLoadingMore: false,
        hasMore: false,
        hasSearched: false,
        isRestored: false,
        error: undefined,
    };

    const signals: SoundsBrowserSignals = {
        isOpen: createSignal(),
        width: createSignal(),
        mode: createSignal(),
        visibleModes: createSignal(),
        seed: createSignal(),
        selected: createSignal(),
        resume: createSignal(),
        query: createSignal(),
        filters: createSignal(),
        results: createSignal(),
        resultsAppended: createSignal(),
        tagSummary: createSignal(),
        isSearching: createSignal(),
        isLoadingMore: createSignal(),
        hasMore: createSignal(),
        error: createSignal(),
        playback: createSignal(),
    };

    // The browse the previous page was showing, if there was one and it is
    // recent.
    const session = loadSoundsBrowserSession();

    const previews = createSamplePreviewCache();
    const player = createSamplePreviewPlayer(core, previews);
    // A page that closed mid-audition left its resource behind; take it out
    // before the user sees it in their library.
    void sweepAuditionResources(core);
    const actions = createSoundsBrowserActions(
        core,
        {
            search: createSpliceSoundsSearchService(core, { resultsPerPage: kResultsPerPage }),
            fetchSamples: createFetchSpliceSamplesService(),
            soundMatch: createSearchWithSoundSimilarService(),
            presets: createAudioToPresetSearchService(),
            library: createLibrarySearchService(),
        },
        previews,
        player,
        prefs,
        state,
        signals,
    );
    const context: SoundsBrowserContext = { state, signals, actions, previews, player };

    // ── Document-level state: open attribute and width variable ─────────────
    const root = document.documentElement;
    // At phone width the open sheet pins the page (see the css); the scroll
    // offset is carried over as the body's `top` and handed back on close.
    let pinnedScrollY: number | undefined;
    const applyOpen = (isOpen: boolean): void => {
        const isNarrow = window.matchMedia(kNarrowViewportQuery).matches;
        if (isOpen && isNarrow && pinnedScrollY === undefined) {
            pinnedScrollY = window.scrollY;
            document.body.style.top = px(-pinnedScrollY);
        }
        root.setAttribute(styles.kSoundsBrowserOpenAttribute, isOpen ? "open" : "closed");
        if (!isOpen && pinnedScrollY !== undefined) {
            document.body.style.top = "";
            window.scrollTo(0, pinnedScrollY);
            pinnedScrollY = undefined;
        }
    };
    const applyWidth = (width_px: number): void => {
        root.style.setProperty(styles.kSoundsBrowserWidthVar, px(width_px));
    };
    root.style.setProperty(styles.kSoundsBrowserRailWidthVar, px(styles.kRailWidth_px));
    // Restore the remembered layout without animating into it: the body's
    // padding transition is for the user's toggle, not for page load.
    document.body.style.transition = "none";
    applyWidth(state.width_px);
    applyOpen(state.isOpen);
    requestAnimationFrame(() => {
        document.body.style.transition = "";
    });
    signals.isOpen.listen(applyOpen);
    signals.width.listen(applyWidth);

    // ── Rail and logo ────────────────────────────────────────────────────────
    // The rail is the closed surface; the panel slides out over it in the
    // same colour. The logo is one fixed button that travels from the rail
    // into the panel header, so it is the same thing in both states.
    const rail = createEl("div", styles.rail);
    const logoButton = createEl(
        "button",
        styles.logo,
        [
            createSvgIcon("spliceLogo", styles.logoIcon),
            // Read only at phone width, where the button is a pill at the
            // foot of the page rather than a logo in a rail.
            createEl("span", styles.logoLabel, "Sounds"),
            createEl("span", styles.logoTooltip, "Browse\nSounds", (tip) => {
                // The button's aria-label already names it.
                tip.setAttribute("aria-hidden", "true");
            }),
        ],
        (el) => {
            el.type = "button";
            el.addEventListener("click", () => {
                const wasOpen = state.isOpen;
                actions.toggle();
                if (!wasOpen) {
                    requestAnimationFrame(() => searchInput.focus());
                }
            });
        },
    );
    const applyLogoLabel = (isOpen: boolean): void => {
        logoButton.setAttribute("aria-label", isOpen ? "Hide Splice Sounds" : "Show Splice Sounds");
        logoButton.setAttribute("aria-expanded", String(isOpen));
        // Closed, the "Browse Sounds" label under it names it; a native title
        // would add a bubble beside that.
        if (isOpen) {
            logoButton.title = "Splice Sounds";
        } else {
            logoButton.removeAttribute("title");
        }
    };
    applyLogoLabel(state.isOpen);
    signals.isOpen.listen(applyLogoLabel);

    // ── Header: title, scope and search ──────────────────────────────────────
    const collapseButton = createEl(
        "button",
        styles.collapseButton,
        [createSvgIcon("x", styles.collapseIcon)],
        (el) => {
            el.type = "button";
            el.setAttribute("aria-label", "Hide Splice Sounds");
            el.title = "Close";
            el.addEventListener("click", actions.close);
        },
    );

    const searchInput = createEl("input", styles.searchInput, [], (el) => {
        // Text, not search: the engine's own clear control would sit beside ours.
        el.type = "text";
        el.setAttribute("aria-label", "Search Splice Sounds");
        el.autocomplete = "off";
        el.spellcheck = false;
        el.addEventListener("input", () => actions.setQuery(el.value));
        el.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                actions.submitQuery();
            }
        });
    });
    const searchClear = createEl("button", styles.searchClear, [createSvgIcon("x")], (el) => {
        el.type = "button";
        el.setAttribute("aria-label", "Clear search");
        el.style.display = "none";
        el.addEventListener("click", () => {
            actions.setQuery("");
            // Clearing the words re-runs a text search; a seeded mode is unmoved.
            if (!isSeededMode(getSoundsBrowserMode(state.modeId))) {
                void actions.search();
            }
            searchInput.focus();
        });
    });
    signals.query.listen((query) => {
        if (searchInput.value !== query) {
            searchInput.value = query;
        }
        searchClear.style.display = query.length > 0 ? "flex" : "none";
    });

    // The scope control: which mode the field is in. One icon in the field,
    // a menu of the modes this visitor can use. Hidden while there is only
    // one, which is what most tiers see until a mode is promoted.
    const scopeIconHost = createEl("span", "", []);
    const scopeButton = createEl(
        "button",
        styles.scopeButton,
        [scopeIconHost, createSvgIcon("chevronDown", styles.scopeChevron)],
        (el) => {
            el.type = "button";
            el.setAttribute("aria-haspopup", "menu");
            el.setAttribute("aria-expanded", "false");
            el.addEventListener("click", (event) => {
                event.stopPropagation();
                setScopeMenuOpen(!isScopeMenuOpen);
            });
        },
    );
    const scopeMenu = createEl("div", styles.scopeMenu, [], (el) => {
        el.setAttribute("role", "menu");
    });
    let isScopeMenuOpen = false;
    const setScopeMenuOpen = (isOpen: boolean): void => {
        isScopeMenuOpen = isOpen;
        scopeMenu.classList.toggle(styles.scopeMenuOpen, isOpen);
        scopeButton.setAttribute("aria-expanded", String(isOpen));
    };
    document.addEventListener("click", (event) => {
        if (isScopeMenuOpen && !scopeMenu.contains(event.target as Node)) {
            setScopeMenuOpen(false);
        }
    });

    // The scope menu lists what the words do. The seeded modes are chosen in
    // the sound-search zone under the field, where their sample lives.
    const renderScopeMenu = (modes: readonly SoundsBrowserMode[]): void => {
        scopeMenu.replaceChildren(
            ...getTextModes(modes).map((mode) =>
                createEl(
                    "button",
                    mode.id === state.modeId
                        ? `${styles.scopeMenuItem} ${styles.scopeMenuItemActive}`
                        : styles.scopeMenuItem,
                    [
                        createSvgIcon(mode.icon, styles.scopeMenuItemIcon),
                        createEl("span", styles.scopeMenuItemText, [
                            createEl("span", styles.scopeMenuItemLabel, mode.label),
                            createEl("span", styles.scopeMenuItemDescription, mode.description),
                        ]),
                    ],
                    (el) => {
                        el.type = "button";
                        el.setAttribute("role", "menuitemradio");
                        el.setAttribute("aria-checked", String(mode.id === state.modeId));
                        el.addEventListener("click", () => {
                            setScopeMenuOpen(false);
                            actions.setMode(mode.id);
                            searchInput.focus();
                        });
                    },
                ),
            ),
        );
    };

    // At phone width there is no drop target for Search with Sound, so the
    // field offers the import directly: a plus that opens the file picker and
    // seeds the search with what is chosen. Hidden on wider screens, where
    // the zone under the field and the scope menu do the job.
    const importButton = createEl("button", styles.importButton, "+", (el) => {
        el.type = "button";
        el.title = "Import a sample to search with";
        el.setAttribute("aria-label", "Import a sample to search with");
        // Only while a seeded mode is on offer, like the zone and the sparkle.
        el.style.display = hasSeededMode(state.visibleModes) ? "" : "none";
        el.addEventListener("click", (event) => {
            event.stopPropagation();
            seedFileInput.click();
        });
    });
    const searchField = createEl("div", styles.searchField, [
        createEl("span", styles.searchIcon, [createSvgIcon("search")]),
        scopeButton,
        importButton,
        searchInput,
        searchClear,
    ]);

    // A seeded mode takes its sample here: a file from disk, a result from
    // the list, or the app's own input dragged back out of its dropzone.
    addFileDropHandling(searchField, {
        onEnter: () => searchField.classList.add(styles.searchFieldDropTarget),
        onLeave: () => searchField.classList.remove(styles.searchFieldDropTarget),
        onDrop: (file) => {
            searchField.classList.remove(styles.searchFieldDropTarget);
            if (file !== undefined) {
                actions.seedFromFile(file);
            }
        },
        onDropSample: (result) => {
            searchField.classList.remove(styles.searchFieldDropTarget);
            actions.seedFromResult(result);
        },
    });

    const searchFieldHost = createEl("div", styles.searchFieldHost, [searchField, scopeMenu]);

    // ── Search with sound ────────────────────────────────────────────────────
    // The zone under the field: a sample dropped or picked here is what the
    // seeded modes search from. It is where those modes are chosen too.
    const seedCard = createSeedCard(core, context, actions.clearSeed);
    const seedModeToggle = createEl("div", styles.seedModeToggle, [], (el) => {
        el.setAttribute("role", "group");
        el.setAttribute("aria-label", "How to search from this sample");
        el.style.display = "none";
    });
    const renderSeedModeToggle = (): void => {
        seedModeToggle.replaceChildren(
            ...getSeededModes(state.visibleModes).map((mode) =>
                createEl(
                    "button",
                    mode.id === state.modeId
                        ? `${styles.seedModeButton} ${styles.seedModeButtonActive}`
                        : styles.seedModeButton,
                    [createEl("span", styles.seedModeLabel, mode.label)],
                    (el) => {
                        el.type = "button";
                        el.title = mode.description;
                        el.setAttribute("aria-pressed", String(mode.id === state.modeId));
                        el.addEventListener("click", () => actions.setMode(mode.id));
                    },
                ),
            ),
        );
    };
    const soundZoneLoaded = createEl("div", styles.soundZoneLoaded, [seedCard.el]);

    const seedFileInput = createAudioFileInputEl(styles.hiddenFileInput, (el) => {
        el.addEventListener("change", () => {
            const file = el.files?.[0];
            if (file !== undefined) {
                actions.seedFromFile(file);
            }
            el.value = "";
        });
    });
    const soundZoneEmpty = createEl(
        "button",
        styles.soundZoneEmpty,
        [
            createSvgIcon("searchSparkles", styles.soundZoneEmptyIcon),
            createEl("span", "", "Drop a sample here to Search with Sound"),
        ],
        (el) => {
            el.type = "button";
            el.addEventListener("click", () => seedFileInput.click());
        },
    );
    const soundZone = createEl(
        "div",
        styles.soundZone,
        [soundZoneEmpty, soundZoneLoaded, seedFileInput],
        (el) => {
            el.setAttribute("role", "group");
            el.setAttribute("aria-label", "Search with sound");
            el.style.display = "none";
        },
    );
    addFileDropHandling(soundZone, {
        onEnter: () => soundZone.classList.add(styles.soundZoneDropTarget),
        onLeave: () => soundZone.classList.remove(styles.soundZoneDropTarget),
        onDrop: (file) => {
            soundZone.classList.remove(styles.soundZoneDropTarget);
            if (file !== undefined) {
                actions.seedFromFile(file);
            }
        },
        onDropSample: (result) => {
            soundZone.classList.remove(styles.soundZoneDropTarget);
            actions.seedFromResult(result);
        },
    });

    const applyMode = (): void => {
        const mode = getSoundsBrowserMode(state.modeId);
        // The field is always the text search. A seeded mode lives in the
        // zone below; the field keeps Search's own call to action and takes
        // words as it always does.
        const fieldMode = isSeededMode(mode)
            ? getSoundsBrowserMode(kDefaultSoundsBrowserModeId)
            : mode;
        searchInput.placeholder = fieldMode.placeholder;
        scopeIconHost.replaceChildren(createSvgIcon(fieldMode.icon, styles.scopeIcon));
        scopeButton.title = fieldMode.label;
        scopeButton.setAttribute("aria-label", `Search mode: ${fieldMode.label}`);
        applyFilterVisibility();
        applyHomeLayout();
        renderScopeMenu(state.visibleModes);
        renderSeedModeToggle();
        applySeed(state.seed);
        // The idle message is the mode's own.
        if (state.results.length === 0 && !state.isSearching) {
            renderResults(state.results);
        }
    };

    // The filters refine a result set, so they appear once one is on screen,
    // not while it is still loading. They stay through a re-search so a pill
    // does not vanish under the pointer, and after a search that found
    // nothing they stay only when a filter is what to change.
    let isFilterRowShown = false;
    const applyFilterVisibility = (): void => {
        const mode = getSoundsBrowserMode(state.modeId);
        const hasResultsShown = state.results.length > 0 && !state.isSearching;
        const hasFiltersToClear =
            state.hasSearched && !state.isSearching && hasActiveFilters(state.filters);
        const isKeptWhileSearching = state.isSearching && isFilterRowShown;
        isFilterRowShown =
            mode.usesFilters && (hasResultsShown || hasFiltersToClear || isKeptWhileSearching);
        filterRow.style.display = isFilterRowShown ? "" : "none";
    };

    // Search with Sound belongs to the catalog: the seeded modes search all of
    // Splice, so the zone and its tabs step aside while the library is the
    // scope. The sample stays in state and is back when the scope is.
    const applySoundZone = (): void => {
        const isShown = hasSeededMode(state.visibleModes) && !state.filters.isLibraryOnly;
        const isLoaded = state.seed !== undefined;
        soundZone.style.display = isShown ? "" : "none";
        soundZone.classList.toggle(styles.soundZoneUnloaded, !isLoaded);
        soundZoneEmpty.style.display = isLoaded ? "none" : "";
        soundZoneLoaded.style.display = isLoaded ? "" : "none";
        seedModeToggle.style.display = isShown && isLoaded ? "" : "none";
    };

    // The sample persists until cleared, whichever mode the field is in; the
    // toggle shows which seeded mode it drives, or none while words lead.
    const applySeed = (seed: SoundsBrowserSeed | undefined): void => {
        applySoundZone();
        renderSeedModeToggle();
        seedCard.setSeed(seed);
        applyHomeLayout();
    };

    signals.visibleModes.listen((modes) => {
        const hasScope = getTextModes(modes).length > 1;
        scopeButton.style.display = hasScope ? "" : "none";
        searchField.dataset.hasScope = String(hasScope);
        importButton.style.display = hasSeededMode(modes) ? "" : "none";
        renderScopeMenu(modes);
        applySoundZone();
        applySeed(state.seed);
    });
    signals.mode.listen(applyMode);
    signals.seed.listen(applySeed);

    // Where a word search looks: all of Splice, or the samples the user has
    // licensed. Two icons in the title row, as the plugin's own navigation
    // puts the catalog and the library side by side.
    const createScopeSwitchButton = (
        icon: "home" | "folder",
        label: string,
        isLibrary: boolean,
    ): HTMLButtonElement =>
        createEl(
            "button",
            styles.scopeSwitchButton,
            [createSvgIcon(icon, styles.scopeSwitchIcon)],
            (el) => {
                el.type = "button";
                el.title = label;
                el.setAttribute("aria-label", label);
                el.setAttribute("aria-pressed", "false");
                el.addEventListener("click", () => actions.setLibraryOnly(isLibrary));
            },
        );
    const allSpliceButton = createScopeSwitchButton("home", "All of Splice", false);
    const libraryButton = createScopeSwitchButton("folder", "Your library", true);
    const scopeSwitch = createEl(
        "div",
        styles.scopeSwitch,
        [allSpliceButton, libraryButton],
        (el) => {
            el.setAttribute("role", "group");
            el.setAttribute("aria-label", "Where to search");
        },
    );
    const applyScopeSwitch = (): void => {
        const isLibrary = state.filters.isLibraryOnly;
        allSpliceButton.classList.toggle(styles.scopeSwitchButtonActive, !isLibrary);
        allSpliceButton.setAttribute("aria-pressed", String(!isLibrary));
        libraryButton.classList.toggle(styles.scopeSwitchButtonActive, isLibrary);
        libraryButton.setAttribute("aria-pressed", String(isLibrary));
    };
    signals.filters.listen(applyScopeSwitch);
    signals.filters.listen(applySoundZone);
    applyScopeSwitch();

    // What we recommend before anything has been searched: today the quick
    // picks; the plan is curated packs and collections. Shown in the home
    // state only, straight under the field.
    const quickPicksRow = createEl(
        "div",
        styles.quickPicks,
        kQuickPicks.map((term) =>
            createEl("button", styles.tagChip, term, (el) => {
                el.type = "button";
                el.addEventListener("click", () => {
                    actions.setQuery(term);
                    void actions.search();
                });
            }),
        ),
        (el) => {
            el.setAttribute("aria-label", "Recommended searches");
        },
    );

    /**
     * The home state: words lead, nothing searched, no sample. The
     * recommendations show and the zone stands taller with room above it.
     * Once there are results, or a sample, the zone takes its working size
     * right under the field.
     */
    const applyHomeLayout = (): void => {
        const mode = getSoundsBrowserMode(state.modeId);
        const isHome =
            mode.input === "text" &&
            !state.hasSearched &&
            !state.isSearching &&
            state.seed === undefined;
        quickPicksRow.style.display = isHome ? "" : "none";
        soundZone.classList.toggle(styles.soundZoneHome, isHome);
    };

    const header = createEl("div", styles.header, [
        createEl("div", styles.titleRow, [
            createEl("h2", styles.title, "Sounds"),
            scopeSwitch,
            collapseButton,
        ]),
        searchFieldHost,
        quickPicksRow,
        soundZone,
        seedModeToggle,
    ]);

    // ── Filters, tag row and sort ────────────────────────────────────────────
    const searchTag = (tag: string): void => {
        actions.toggleTag(tag);
    };
    const { filterRow, tagRow, sortControls } = createSoundsBrowserFilters(context);

    // ── Results ──────────────────────────────────────────────────────────────
    const columnLabels = [
        createEl("span", `${styles.metaCell} ${styles.metaKey} ${styles.columnLabel}`, "Key"),
        createEl("span", `${styles.metaCell} ${styles.metaBpm} ${styles.columnLabel}`, "BPM"),
        createEl("span", `${styles.metaCell} ${styles.metaTime} ${styles.columnLabel}`, "Time"),
    ];
    const columnHeaders = createEl("div", styles.columnHeaders, [
        sortControls,
        createEl("span", styles.columnHeaderSpacer),
        ...columnLabels,
    ]);

    const errorEl = createEl("div", styles.errorMessage, "", (el) => {
        el.style.display = "none";
    });
    signals.error.listen((error) => {
        errorEl.textContent = error ?? "";
        errorEl.style.display = error !== undefined ? "" : "none";
    });

    const footerEl = createEl("div", styles.listFooter, "");
    const listEl = createEl("div", styles.list, [], (el) => {
        el.addEventListener("scroll", () => actions.setScrollOffset(el.scrollTop), {
            passive: true,
        });
    });

    let rows: SampleRowComponent[] = [];

    // Previews are fetched as rows come into view, a screen ahead, so the
    // waveforms are there by the time the eye is.
    const warmObserver = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }
                const uuid = (entry.target as HTMLElement).dataset.uuid;
                rows.find((row) => row.result.metadata.uuid === uuid)?.warm();
                warmObserver.unobserve(entry.target);
            }
        },
        { root: listEl, rootMargin: "400px 0px" },
    );

    const loadMoreObserver = new IntersectionObserver(
        (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                void actions.loadMore();
            }
        },
        { root: listEl, rootMargin: "300px 0px" },
    );
    loadMoreObserver.observe(footerEl);

    // Waveforms are drawn at the row's width, so a resize redraws them.
    const resizeObserver = new ResizeObserver(() => {
        for (const row of rows) {
            row.redraw();
        }
    });
    resizeObserver.observe(listEl);

    const clearRows = (): void => {
        for (const row of rows) {
            warmObserver.unobserve(row.el);
            row.dispose();
        }
        rows = [];
    };

    // ── Tap to use ───────────────────────────────────────────────────────────
    // One target takes the sample at once; several open a chooser under the
    // row's button. At phone width the sheet then gets out of the way and the
    // target scrolls into view, so the sample is seen to land.
    //
    // A dropzone that opened the dock itself is the target for as long as the
    // dock stays open: the person has already said where the sample goes, so
    // the chooser would only ask again. Forgotten on close, so the pill opens
    // a dock with no favourite.
    let preferredTarget: SampleTarget | undefined;
    registerSoundsBrowserOpener(({ target }) => {
        preferredTarget = target;
        actions.open();
    });
    signals.isOpen.listen((isOpen) => {
        if (!isOpen) {
            preferredTarget = undefined;
        }
    });
    let useMenu: HTMLElement | undefined;
    const closeUseMenu = (): void => {
        useMenu?.remove();
        useMenu = undefined;
    };
    const deliverSample = (target: SampleTarget, result: SpliceSearchResult): void => {
        closeUseMenu();
        target.accept(result);
        if (window.matchMedia(kNarrowViewportQuery).matches) {
            actions.close();
            target.el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    };
    const useSample = (result: SpliceSearchResult, anchor: HTMLElement): void => {
        const targets = getSampleTargets();
        if (targets.length === 0) {
            return;
        }
        if (preferredTarget !== undefined && targets.includes(preferredTarget)) {
            deliverSample(preferredTarget, result);
            return;
        }
        if (targets.length === 1) {
            deliverSample(targets[0], result);
            return;
        }
        closeUseMenu();
        const rect = anchor.getBoundingClientRect();
        const menu = createEl(
            "div",
            styles.useMenu,
            [
                createEl("p", styles.useMenuTitle, "Use in"),
                ...targets.map((target) =>
                    createEl("button", styles.useMenuItem, target.label, (el) => {
                        el.type = "button";
                        el.addEventListener("click", (event) => {
                            event.stopPropagation();
                            deliverSample(target, result);
                        });
                    }),
                ),
            ],
            (el) => {
                el.setAttribute("role", "menu");
                el.style.top = px(rect.bottom + 6);
                // Right-aligned to the button, kept inside the viewport.
                el.style.right = px(Math.max(8, window.innerWidth - rect.right));
            },
        );
        useMenu = menu;
        document.body.appendChild(menu);
        // Below the button when there is room, above it otherwise, never off
        // the bottom of the screen for a row low in the list.
        const menuHeight = menu.offsetHeight;
        if (rect.bottom + 6 + menuHeight > window.innerHeight - 8) {
            menu.style.top = px(Math.max(8, rect.top - 6 - menuHeight));
        }
    };
    document.addEventListener("pointerdown", (event) => {
        if (
            useMenu !== undefined &&
            event.target instanceof Node &&
            !useMenu.contains(event.target)
        ) {
            closeUseMenu();
        }
    });

    const appendRows = (results: readonly SpliceSearchResult[]): HTMLElement[] => {
        const created = results.map((result, index) => {
            const row = createSampleRow(context, result, {
                onTagClick: searchTag,
                onFindSimilar: (sample) => actions.seedFromResult(sample, "siso"),
                onUse: useSample,
            });
            // A short cascade down the batch, capped so a long page does not
            // keep its last rows waiting.
            row.el.style.animationDelay = `${Math.min(index, 10) * 16}ms`;
            return row;
        });
        rows.push(...created);
        for (const row of created) {
            warmObserver.observe(row.el);
        }
        return created.map((row) => row.el);
    };

    const createStateMessage = (title: string, body: string): HTMLElement =>
        createEl("div", styles.stateMessage, [
            createEl("p", styles.stateMessageTitle, title),
            createEl("p", styles.stateMessageBody, body),
        ]);

    // The home state: the recommendations sit in the header under the field,
    // and the list is empty. A mode with instructions shows them in the list.
    const renderIdle = (): void => {
        clearRows();
        const mode = getSoundsBrowserMode(state.modeId);
        if (mode.emptyBody === undefined) {
            listEl.replaceChildren();
            return;
        }
        listEl.replaceChildren(createStateMessage(mode.emptyTitle, mode.emptyBody));
    };

    const renderResults = (results: readonly SpliceSearchResult[]): void => {
        clearRows();
        if (results.length === 0) {
            if (!state.hasSearched || state.error !== undefined) {
                renderIdle();
                return;
            }
            const mode = getSoundsBrowserMode(state.modeId);
            if (isSeededMode(mode) && state.seed === undefined) {
                renderIdle();
                return;
            }
            listEl.replaceChildren(
                createStateMessage(
                    "No sounds found",
                    mode.usesFilters && hasActiveFilters(state.filters)
                        ? "Try a different search or clear a filter."
                        : "Try a different search.",
                ),
            );
            return;
        }
        listEl.replaceChildren(...appendRows(results), footerEl);
        listEl.scrollTop = 0;
    };

    // The header row is part of the list: there when there are rows, gone
    // (not merely invisible) when there are none, so the empty state sits
    // straight under the filters.
    const updateColumnHeaders = (): void => {
        columnHeaders.style.display = state.results.length > 0 ? "" : "none";
        // Presets have no key, tempo or length, so the labels go with them.
        const hasColumns = state.modeId !== "presets";
        for (const label of columnLabels) {
            label.style.display = hasColumns ? "" : "none";
        }
    };

    signals.results.listen((results) => {
        if (!state.isSearching) {
            renderResults(results);
        }
        updateColumnHeaders();
        applyFilterVisibility();
        applyHomeLayout();
    });
    signals.resultsAppended.listen((appended) => {
        const els = appendRows(appended);
        footerEl.before(...els);
    });
    signals.isSearching.listen((isSearching) => {
        if (isSearching) {
            clearRows();
            listEl.replaceChildren(
                createEl("div", styles.stateMessage, [createWaveSpinner("Searching")]),
            );
        } else {
            renderResults(state.results);
        }
        updateColumnHeaders();
        applyFilterVisibility();
        applyHomeLayout();
    });
    const updateFooter = (): void => {
        if (state.isLoadingMore) {
            footerEl.textContent = "Loading more…";
        } else if (state.results.length > 0 && !state.hasMore) {
            footerEl.textContent = "That's everything for this search.";
        } else {
            footerEl.textContent = "";
        }
    };
    signals.isLoadingMore.listen(updateFooter);
    signals.hasMore.listen(updateFooter);
    updateColumnHeaders();
    applyMode();

    // Rendered from storage, not from a search, and scrolled back to where
    // it was, or to the sample that was selected when the list had not been
    // scrolled.
    if (session !== undefined) {
        actions.restoreSession(session);
        listEl.scrollTop = session.scrollTop;
        if (session.scrollTop === 0 && session.selectedUuid !== undefined) {
            rows.find(
                (row) => row.result.metadata.uuid === session.selectedUuid,
            )?.el.scrollIntoView({ block: "center" });
        }
        actions.tryResume();
    }
    // Remembered open with no browse to restore: list the library now, as a
    // fresh open would (see `searchIfUnstarted`). Opening later does the same
    // through `actions.open`.
    if (state.isOpen) {
        actions.searchIfUnstarted();
    }

    // Which modes this visitor gets, now and whenever the audience changes
    // (the flags resolving, or an R&D user previewing another tier).
    audienceStateSignal.listenAndInit(actions.applyAudience);

    // ── Resize handle ────────────────────────────────────────────────────────
    const resizeHandle = createEl("div", styles.resizeHandle, [], (el) => {
        el.setAttribute("role", "separator");
        el.setAttribute("aria-orientation", "vertical");
        el.addEventListener("pointerdown", (event) => {
            event.preventDefault();
            el.setPointerCapture(event.pointerId);
            root.setAttribute(styles.kSoundsBrowserOpenAttribute, "resizing");
            const onMove = (moveEvent: PointerEvent): void => {
                actions.setWidth(moveEvent.clientX);
            };
            const onUp = (): void => {
                el.removeEventListener("pointermove", onMove);
                el.removeEventListener("pointerup", onUp);
                el.removeEventListener("pointercancel", onUp);
                applyOpen(state.isOpen);
            };
            el.addEventListener("pointermove", onMove);
            el.addEventListener("pointerup", onUp);
            el.addEventListener("pointercancel", onUp);
        });
    });

    // ── Panel ────────────────────────────────────────────────────────────────
    const panel = createEl(
        "aside",
        styles.panel,
        [
            createEl("div", styles.panelBody, [
                header,
                filterRow,
                tagRow,
                columnHeaders,
                errorEl,
                listEl,
            ]),
            resizeHandle,
        ],
        (el) => {
            el.setAttribute("aria-label", "Splice Sounds");
            el.addEventListener("keydown", (event) => {
                if (event.key === "Escape") {
                    if (isScopeMenuOpen) {
                        setScopeMenuOpen(false);
                        return;
                    }
                    actions.close();
                }
            });
        },
    );
    signals.isOpen.listen((isOpen) => {
        panel.setAttribute("aria-hidden", String(!isOpen));
    });
    // At phone width the sheet can be swiped away: from its header, or from
    // the list once that is scrolled to the top.
    attachSheetSwipe(panel, {
        handles: [header],
        scrollEl: listEl,
        isEnabled: () => window.matchMedia(kNarrowViewportQuery).matches,
        onDismiss: actions.close,
    });
    panel.setAttribute("aria-hidden", String(!state.isOpen));

    // Leaving the page: what is playing is set aside for the next page to
    // resume; otherwise the audition resource is removed. Tagged either way,
    // so a close that beats the delete is swept on the next visit.
    window.addEventListener("pagehide", actions.saveForNavigation);

    // The scrim exists at phone width only (its style hides it elsewhere): a
    // tap on the dimmed page puts the sheet away.
    const scrim = createEl("button", styles.scrim, [], (el) => {
        el.type = "button";
        el.tabIndex = -1;
        el.setAttribute("aria-label", "Hide Splice Sounds");
        el.addEventListener("click", actions.close);
    });
    document.body.append(rail, scrim, panel, logoButton);
};
